import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import districtData from "../../../assets/districts.json";
import { adminGet, updateAdminMemberFullProfileApi } from "../../../Services";

const STATES = [
  "Andaman and Nicobar",
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chandigarh",
  "Chhattisgarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Ladakh",
  "Lakshadweep",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Puducherry",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const DATE_FIELDS = new Set(["dob", "member_since", "expiry_date"]);

const toInputDate = (val) => {
  if (val == null || val === "") return "";
  const s = String(val);
  return s.length >= 10 ? s.slice(0, 10) : "";
};

const getMemberDesignationId = (member) => {
  const a = member?.DesignationAssignments?.[0];
  if (!a) return "";
  const raw =
    a.designation_id ??
    a.designationId ??
    a.Designation?.id;
  if (raw != null) return String(raw);
  return "";
};

const snapshotFromMember = (m) => ({
  name: m?.name ?? "",
  dob: toInputDate(m?.dob),
  profession: m?.profession ?? "",
  block: m?.block ?? "",
  district: m?.district ?? "",
  state: m?.state ?? "",
  pin: m?.pin != null ? String(m.pin) : "",
  aadhaar_no: m?.aadhaar_no != null ? String(m.aadhaar_no) : "",
  mobile_no: m?.mobile_no != null ? String(m.mobile_no) : "",
  email: m?.email ?? "",
  vehicle_no: m?.vehicle_no ?? "",
  remarks: m?.remarks ?? "",
  member_since: toInputDate(m?.member_since),
  expiry_date: toInputDate(m?.expiry_date),
  designation_id: getMemberDesignationId(m),
});

const normalizeForCompare = (key, val) => {
  if (DATE_FIELDS.has(key)) return toInputDate(val);
  if (key === "designation_id") return val === "" || val == null ? "" : String(val);
  if (key === "email") return (val ?? "").trim();
  if (key === "vehicle_no" || key === "remarks") return (val ?? "").trim();
  if (key === "pin" || key === "aadhaar_no" || key === "mobile_no")
    return val == null || val === "" ? "" : String(val).trim();
  return (val ?? "").trim();
};

const TEXT_KEYS = [
  "name",
  "dob",
  "profession",
  "block",
  "district",
  "state",
  "pin",
  "aadhaar_no",
  "mobile_no",
  "email",
  "vehicle_no",
  "remarks",
  "member_since",
  "expiry_date",
  "designation_id",
];

function validateForm(snapshot, current) {
  const mob = (current.mobile_no || "").trim();
  const snapMob = (snapshot.mobile_no || "").trim();
  if (mob === "" && snapMob !== "") {
    return "Mobile number cannot be cleared once set.";
  }
  const ad = (current.aadhaar_no || "").trim();
  const snapAd = (snapshot.aadhaar_no || "").trim();
  if (ad === "" && snapAd !== "") {
    return "Aadhaar number cannot be cleared once set.";
  }
  if (mob && !/^[0-9]{10}$/.test(mob)) {
    return "Mobile number must be exactly 10 digits.";
  }
  if (ad && !/^[0-9]{12}$/.test(ad)) {
    return "Aadhaar number must be exactly 12 digits.";
  }
  return null;
}

function buildFormData(snapshot, current, photoFile, aadhaarFile) {
  const fd = new FormData();
  let count = 0;

  for (const key of TEXT_KEYS) {
    const cur = normalizeForCompare(key, current[key]);
    const old = normalizeForCompare(key, snapshot[key]);
    if (cur === old) continue;
    if (key === "designation_id") {
      fd.append("designation_id", cur === "" ? "" : String(cur));
    } else {
      fd.append(key, cur);
    }
    count++;
  }
  if (photoFile) {
    fd.append("photo", photoFile);
    count++;
  }
  if (aadhaarFile) {
    fd.append("aadhaar", aadhaarFile);
    count++;
  }
  return { fd, count };
}

/**
 * Admin full-profile editor (restricted account on backend).
 */
function EditMemberFullProfileModal({ show, onHide, member, onSuccess }) {
  const snapshotRef = useRef(null);
  const [form, setForm] = useState(() => snapshotFromMember({}));
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show || !member?.id) return;
    const snap = snapshotFromMember(member);
    snapshotRef.current = snap;
    setForm(snap);
    setPhotoFile(null);
    setAadhaarFile(null);

    if (snap.state) {
      const filtered = districtData.districts.filter((d) => d.state === snap.state);
      setDistricts(filtered);
    } else {
      setDistricts([]);
    }

    let cancelled = false;
    (async () => {
      if (!snap.state || !snap.district) {
        if (!cancelled) setBlocks([]);
        return;
      }
      try {
        const blocksFile = await import(
          `../../../assets/BlocksList/${snap.state.replace(/\s/g, "")}.json`
        );
        const districtBlocks = blocksFile[snap.district] || [];
        if (!cancelled) setBlocks(districtBlocks);
      } catch {
        if (!cancelled) setBlocks([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, member]);

  useEffect(() => {
    if (!show) return;
    adminGet("/api/designations/getAll")
      .then((res) => setDesignations(res.data || []))
      .catch(() => toast.error("Could not load designations."));
  }, [show]);

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setForm((prev) => ({
      ...prev,
      state: selectedState,
      district: "",
      block: "",
    }));
    const filtered = districtData.districts.filter((d) => d.state === selectedState);
    setDistricts(filtered);
    setBlocks([]);
  };

  const handleDistrictChange = async (e) => {
    const selectedDistrict = e.target.value;
    const stateVal = form.state;
    setForm((prev) => ({
      ...prev,
      district: selectedDistrict,
      block: "",
    }));
    if (!stateVal) {
      setBlocks([]);
      return;
    }
    try {
      const blocksFile = await import(
        `../../../assets/BlocksList/${stateVal.replace(/\s/g, "")}.json`
      );
      const districtBlocks = blocksFile[selectedDistrict] || [];
      setBlocks(districtBlocks);
    } catch {
      setBlocks([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "photo" && files?.[0]) {
      setPhotoFile(files[0]);
      return;
    }
    if (name === "aadhaar" && files?.[0]) {
      setAadhaarFile(files[0]);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const snapshot = snapshotRef.current;
    if (!snapshot || !member?.id) return;

    const err = validateForm(snapshot, form);
    if (err) {
      toast.error(err);
      return;
    }

    const { fd, count } = buildFormData(snapshot, form, photoFile, aadhaarFile);
    if (count === 0) {
      toast.warning("No changes to save.");
      return;
    }

    setSaving(true);
    try {
      const res = await updateAdminMemberFullProfileApi(member.id, fd);
      toast.success(res.data?.message || "Member profile updated");
      onSuccess?.(res.data?.member);
      onHide();
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message ||
        "Update failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Edit member full profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Only changed fields are sent. Registration ID and approval status are not edited here.
        </p>
        <Form>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Full name</Form.Label>
              <Form.Control name="name" value={form.name} onChange={handleChange} />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Date of birth</Form.Label>
              <Form.Control type="date" name="dob" value={form.dob} onChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Profession</Form.Label>
              <Form.Control name="profession" value={form.profession} onChange={handleChange} />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Designation</Form.Label>
              <Form.Select
                name="designation_id"
                value={form.designation_id}
                onChange={handleChange}
              >
                <option value="">Clear designation</option>
                {designations.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.scope} — {d.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-2">
              <Form.Label>State</Form.Label>
              <Form.Select name="state" value={form.state} onChange={handleStateChange}>
                <option value="">— Select —</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>District</Form.Label>
              <Form.Select
                name="district"
                value={form.district}
                onChange={handleDistrictChange}
                disabled={!form.state}
              >
                <option value="">— Select —</option>
                {districts.map((d) => (
                  <option key={d.district} value={d.district}>
                    {d.district}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Block</Form.Label>
              <Form.Select
                name="block"
                value={form.block}
                onChange={handleChange}
                disabled={!form.district}
              >
                <option value="">— Select —</option>
                {blocks.map((b, idx) => (
                  <option key={idx} value={b}>
                    {b}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>PIN</Form.Label>
              <Form.Control name="pin" value={form.pin} onChange={handleChange} />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Vehicle no.</Form.Label>
              <Form.Control name="vehicle_no" value={form.vehicle_no} onChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Aadhaar no.</Form.Label>
              <Form.Control name="aadhaar_no" value={form.aadhaar_no} onChange={handleChange} />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Mobile no.</Form.Label>
              <Form.Control name="mobile_no" value={form.mobile_no} onChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-2">
              <Form.Label>Email (leave empty to clear)</Form.Label>
              <Form.Control type="email" name="email" value={form.email} onChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-2">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>Member since</Form.Label>
              <Form.Control
                type="date"
                name="member_since"
                value={form.member_since}
                onChange={handleChange}
              />
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>Expiry date</Form.Label>
              <Form.Control
                type="date"
                name="expiry_date"
                value={form.expiry_date}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-2">
              <Form.Label>New profile photo</Form.Label>
              <Form.Control
                type="file"
                name="photo"
                accept="image/*"
                onChange={handleChange}
              />
              {photoFile && (
                <Form.Text className="text-success">Selected: {photoFile.name}</Form.Text>
              )}
            </Col>
            <Col md={6} className="mb-2">
              <Form.Label>New Aadhaar image</Form.Label>
              <Form.Control
                type="file"
                name="aadhaar"
                accept="image/*"
                onChange={handleChange}
              />
              {aadhaarFile && (
                <Form.Text className="text-success">Selected: {aadhaarFile.name}</Form.Text>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditMemberFullProfileModal;
